import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SHA256 from "crypto-js/sha256";
import "./BoardTable.css";

const BoardTable = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdmin = searchParams.get("admin") === "true";

  // 상태 관리
  const [boardData, setBoardData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const itemsPerPage = 10; // 페이지당 게시글 수

  // 게시글 데이터 불러오기
  useEffect(() => {
    fetchBoardData(currentPage);
  }, [currentPage]);

  const fetchBoardData = async (page) => {
    try {
      setLoading(true);
      setError(null);

      // 실제 API 호출 (PHP 서버 8080 포트)
      const response = await fetch(
        `https://mrlo.co.kr/api/get-posts.php?page=${page}&limit=${itemsPerPage}`,
      );
      const result = await response.json();

      if (result.success) {
        setBoardData(result.data);
        setTotalPages(result.totalPages);
        setTotalCount(parseInt(result.total));
      } else {
        setError(result.message || "데이터를 불러오는데 실패했습니다.");
      }
    } catch (err) {
      setError("서버와 통신 중 오류가 발생했습니다.");
      console.error("Error fetching board data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageClick = (pageNum) => {
    setCurrentPage(pageNum);
  };

  const handleRowClick = (id) => {
    // admin 파라미터 유지
    const adminParam = isAdmin ? "?admin=true" : "";
    navigate(`/board/${id}${adminParam}`);
  };

  const handleWriteClick = async (e) => {
    e.preventDefault();
    const password = prompt("비밀번호를 입력하세요:");

    if (password === null) {
      // 취소 버튼 클릭 시
      return;
    }

    try {
      // 비밀번호 SHA256 해싱
      const hashedPassword = SHA256(password).toString();

      // 실제 API로 비밀번호 확인
      const response = await fetch("https://mrlo.co.kr/api/verify-admin.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ password: hashedPassword }),
      });

      const result = await response.json();

      if (result.success) {
        // 비밀번호 일치 시 글쓰기 페이지로 이동
        navigate("/board/write?admin=true");
      } else {
        // 비밀번호 불일치 시
        alert(result.message || "비밀번호가 일치하지 않습니다.");
      }
    } catch (err) {
      alert("서버와 통신 중 오류가 발생했습니다.");
      console.error("Error verifying password:", err);
    }
  };

  // 로딩 중일 때
  if (loading) {
    return (
      <div className="board-table-wrapper">
        <div className="board-header">
          <h2 className="board-title">게시판</h2>
        </div>
        <div style={{ textAlign: "center", padding: "50px 0", color: "#666" }}>
          로딩 중...
        </div>
      </div>
    );
  }

  // 에러 발생 시
  if (error) {
    return (
      <div className="board-table-wrapper">
        <div className="board-header">
          <h2 className="board-title">게시판</h2>
        </div>
        <div
          style={{ textAlign: "center", padding: "50px 0", color: "#ff0000" }}
        >
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="board-table-wrapper" style={{ padding: "120px 15px" }}>
      <div>
        <h3
          style={{
            color: "#313337",
            fontSize: window.innerWidth >= 768 ? "38px" : "32px",
            fontWeight: "700",
          }}
        >
          게시판
        </h3>
        <div
          className="board-description"
          style={{
            margin: "20px 0px 60px",
            color: "#000",
            lineHeight: "1.6",
            fontSize: window.innerWidth >= 768 ? "24px" : "18px",
          }}
        >
          엠알메탈로의 게시판입니다. <br />
          엠알메탈로의 최신소식을 확인해 보실 수 있습니다.
        </div>
      </div>

      <div className="board-header">
        <h2 className="board-title">게시판</h2>
        {isAdmin && (
          <button onClick={handleWriteClick} className="write-button">
            글쓰기
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="21"
              height="21"
              viewBox="0 0 21 21"
              fill="none"
            >
              <path
                d="M19.121 0.878359C18.5584 0.315946 17.7955 0 17 0C16.2045 0 15.4416 0.315946 14.879 0.878359L13.199 2.55836L5.293 10.4644C5.16494 10.5924 5.07404 10.7527 5.03 10.9284L4.03 14.9284C3.98803 15.0959 3.99018 15.2715 4.03626 15.438C4.08234 15.6045 4.17077 15.7563 4.29293 15.8784C4.41509 16.0006 4.56681 16.089 4.73331 16.1351C4.89982 16.1812 5.07542 16.1833 5.243 16.1414L9.243 15.1414C9.41862 15.0973 9.57899 15.0064 9.707 14.8784L17.556 7.03036L19.293 5.29236C19.8554 4.72978 20.1714 3.96685 20.1714 3.17136C20.1714 2.37587 19.8554 1.61294 19.293 1.05036L19.121 0.878359ZM16.293 2.29236C16.4805 2.10489 16.7348 1.99957 17 1.99957C17.2652 1.99957 17.5195 2.10489 17.707 2.29236L17.879 2.46436C18.0665 2.65189 18.1718 2.9062 18.1718 3.17136C18.1718 3.43652 18.0665 3.69083 17.879 3.87836L16.862 4.89536L15.307 3.27836L16.293 2.29236ZM13.893 4.69236L15.448 6.30936L8.488 13.2684L6.374 13.7974L6.903 11.6824L13.893 4.69236ZM2 6.17136C2 5.90614 2.10536 5.65179 2.29289 5.46425C2.48043 5.27672 2.73478 5.17136 3 5.17136H8C8.26522 5.17136 8.51957 5.066 8.70711 4.87847C8.89464 4.69093 9 4.43658 9 4.17136C9 3.90614 8.89464 3.65179 8.70711 3.46425C8.51957 3.27672 8.26522 3.17136 8 3.17136H3C2.20435 3.17136 1.44129 3.48743 0.87868 4.05004C0.316071 4.61265 0 5.37571 0 6.17136V17.1714C0 17.967 0.316071 18.7301 0.87868 19.2927C1.44129 19.8553 2.20435 20.1714 3 20.1714H14C14.7956 20.1714 15.5587 19.8553 16.1213 19.2927C16.6839 18.7301 17 17.967 17 17.1714V12.1714C17 11.9061 16.8946 11.6518 16.7071 11.4643C16.5196 11.2767 16.2652 11.1714 16 11.1714C15.7348 11.1714 15.4804 11.2767 15.2929 11.4643C15.1054 11.6518 15 11.9061 15 12.1714V17.1714C15 17.4366 14.8946 17.6909 14.7071 17.8785C14.5196 18.066 14.2652 18.1714 14 18.1714H3C2.73478 18.1714 2.48043 18.066 2.29289 17.8785C2.10536 17.6909 2 17.4366 2 17.1714V6.17136Z"
                fill="white"
              />
            </svg>
          </button>
        )}
      </div>

      <table className="board-table">
        <thead>
          <tr>
            <th className="col-no">NO</th>
            <th className="col-title">제목</th>
            <th className="col-date">일자</th>
          </tr>
        </thead>
        <tbody>
          {boardData.length === 0 ? (
            <tr>
              <td
                colSpan="3"
                style={{
                  textAlign: "center",
                  padding: "50px 0",
                  color: "#999",
                }}
              >
                게시글이 없습니다.
              </td>
            </tr>
          ) : (
            boardData.map((item, index) => {
              // 역순 넘버링: 전체 개수 - ((현재페이지-1) * 페이지당개수 + 인덱스)
              const displayNumber =
                totalCount - ((currentPage - 1) * itemsPerPage + index);
              return (
                <tr
                  key={item.id}
                  onClick={() => handleRowClick(item.id)}
                  className="clickable-row"
                  style={{ cursor: "pointer" }}
                >
                  <td className="col-no">{displayNumber}</td>
                  <td className="col-title">{item.title}</td>
                  <td className="col-date">{item.created_at}</td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      <div className="pagination" style={{ marginBottom: "50px" }}>
        <button
          className="page-button prev"
          onClick={() => handlePageClick(currentPage - 1)}
          disabled={currentPage === 1}
        >
          &lt;
        </button>
        {[...Array(totalPages)].map((_, index) => {
          const pageNum = index + 1;
          return (
            <button
              key={pageNum}
              className={`page-button ${currentPage === pageNum ? "active" : ""}`}
              onClick={() => handlePageClick(pageNum)}
            >
              {pageNum}
            </button>
          );
        })}
        <button
          className="page-button next"
          onClick={() => handlePageClick(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          &gt;
        </button>
      </div>
    </div>
  );
};

export default BoardTable;
